import { ApplicationConfig, DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import {
    AppInsightsErrorHandler,
    authInterceptor,
    getPersistedCountrySync,
    languageInterceptor,
    loadingInterceptor,
    provideDictionaryRefreshOnStart,
    provideTelemetryPolicy,
    StorageService,
    timezoneInterceptor,
    ToastService,
    upstreamUnavailableInterceptor,
    WebStorageService,
    WebToastService,
} from '@raptor-fe/services';
import { provideRouter, withHashLocation } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeAu from '@angular/common/locales/en-AU';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    API_PATHS_TOKEN,
    APP_INSIGHTS_CONNECTION_STRING,
    AUDIT_API_BASE_URL_TOKEN,
    BASE_REPORT_URL_TOKEN,
    BASE_URL_TOKEN,
    BRAND_CONFIG,
    CLIENT_TYPE_TOKEN,
    ClientType,
    EXTERNAL_VEHICLE_LOOKUP_LOGO_TOKEN,
    EXTERNAL_VEHICLE_LOOKUP_NAME_TOKEN,
    I18N_OPTIONS_TOKEN,
    I18nOptions,
    LABOR_ENABLED,
    provideCountryLocale,
    STAFF_SIGN_IN_ENABLED,
    TELEMETRY_CONFIG,
    VEHICLE_SPECIFICATION_AUTO_FETCH_TOKEN,
    WEB_TELEMETRY_SETTING_KEY,
} from '@raptor-fe/core';
import { environment } from '../environments/environment';
import { createWebTelemetryConfig } from './telemetry.config';

registerLocaleData(localeAu);

export const appConfig: ApplicationConfig = {
    providers: [
        {
            provide: LOCALE_ID,
            useFactory: (opts: I18nOptions): string => opts.locale,
            deps: [I18N_OPTIONS_TOKEN],
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: (opts: I18nOptions): string => opts.currencyCode,
            deps: [I18N_OPTIONS_TOKEN],
        },
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withHashLocation()),
        provideHttpClient(withFetch(), withInterceptors([timezoneInterceptor, languageInterceptor, authInterceptor, loadingInterceptor, upstreamUnavailableInterceptor])),
        provideAnimations(),
        provideDictionaryRefreshOnStart(),
        {
            provide: BASE_URL_TOKEN,
            useValue: environment.baseUrl,
        },
        {
            provide: API_PATHS_TOKEN,
            useValue: environment.apiPaths,
        },
        {
            provide: STAFF_SIGN_IN_ENABLED,
            useValue: environment.staffSignInEnabled,
        },
        {
            provide: LABOR_ENABLED,
            useValue: false,
        },
        {
            provide: ToastService,
            useClass: WebToastService,
        },
        {
            provide: StorageService,
            useClass: WebStorageService,
        },
        {
            provide: EXTERNAL_VEHICLE_LOOKUP_LOGO_TOKEN,
            useValue: environment.externalVehicleLookupLogo,
        },
        {
            provide: EXTERNAL_VEHICLE_LOOKUP_NAME_TOKEN,
            useValue: environment.externalVehicleLookupName,
        },
        {
            provide: VEHICLE_SPECIFICATION_AUTO_FETCH_TOKEN,
            useValue: true,
        },
        ...provideCountryLocale(getPersistedCountrySync, 'AU'),
        {
            provide: CLIENT_TYPE_TOKEN,
            useValue: ClientType.WEB,
        },
        {
            provide: BASE_REPORT_URL_TOKEN,
            useValue: environment.baseReportUrl,
        },
        {
            provide: AUDIT_API_BASE_URL_TOKEN,
            useValue: environment.auditApiBaseUrl,
        },
        {
            provide: BRAND_CONFIG,
            useValue: {
                logoPath: 'logo-blue.svg',
                brandName: 'Marlin',
                productName: 'Marlin GTX',
            },
        },
        {
            provide: APP_INSIGHTS_CONNECTION_STRING,
            useValue: environment.appInsightsConnectionString ?? '',
        },
        {
            provide: TELEMETRY_CONFIG,
            useValue: createWebTelemetryConfig(environment),
        },
        {
            provide: ErrorHandler,
            useClass: AppInsightsErrorHandler,
        },
        provideTelemetryPolicy(WEB_TELEMETRY_SETTING_KEY, { customerSessions: true }),
    ],
};
